'use client';

import React, { type ReactNode, useMemo, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PageSection, SectionPill } from '@/components/site/page-shell';
import { PriceCheckWidget, type PriceCheckOption } from '@/components/marketplace-intelligence/PriceCheckWidget';
import type { MarketplaceIntelligenceDashboardPayload } from '@/lib/marketplace-intelligence-dashboard';
import { buildTrendChartGeometry, TREND_CHART_DIMENSIONS } from '@/lib/marketplace-trend-chart';
import type { Marketplace } from '@/lib/marketplace-intelligence';
import type { MarketplaceDecisionInsight, MarketplaceDecisionType } from '@/lib/marketplace-intelligence-decision-insights';
import { localizeMarketplaceIntelligenceLimitation } from '@/lib/marketplace-intelligence-public-copy';
import { formatCountRu } from '@/lib/russian-plural';
import { formatMoscowDateTime, formatRuIsoDate } from '@/lib/time';
import { useNavAutoHide } from '@/hooks/use-nav-auto-hide';
import { useStuckAttribute } from '@/hooks/use-stuck-attribute';

type FilterValue<T extends string> = 'all' | T;

const MARKETPLACE_LABELS: Record<Marketplace, string> = {
  ozon: 'Ozon',
  wildberries: 'Wildberries',
  yandex_market: 'Яндекс Маркет',
};

const CATEGORY_LABELS: Record<string, string> = {
  beauty: 'Красота',
  home: 'Дом',
  apparel: 'Одежда',
  kids: 'Детские товары',
  electronics: 'Электроника',
  pets: 'Товары для животных',
  household_chemicals: 'Бытовая химия',
};

const STATUS_LABELS: Record<string, string> = {
  complete: 'Данные полные',
  partial_with_quality_notes: 'Есть ограничения данных',
};

const JOB_STATUS_LABELS: Record<string, string> = {
  complete: 'собрано',
  partial: 'частично собрано',
  blocked: 'заблокировано',
  incident: 'инцидент',
};

const DECISION_TYPE_LABELS: Record<MarketplaceDecisionType, string> = {
  monitor_competition: 'Мониторить конкуренцию',
  price_positioning: 'Проверить цену',
  strengthen_card: 'Усилить карточку',
  data_quality_hold: 'Не принимать решение',
};

const CONFIDENCE_LABELS: Record<MarketplaceDecisionType, Record<MarketplaceDecisionInsight['confidence'], string>> = {
  monitor_competition: {
    high: 'высокая уверенность в конкуренции',
    medium: 'средняя уверенность в конкуренции',
    low: 'низкая уверенность в конкуренции',
  },
  price_positioning: {
    high: 'высокая уверенность в цене',
    medium: 'средняя уверенность в цене',
    low: 'низкая уверенность в цене',
  },
  strengthen_card: {
    high: 'высокая уверенность в рейтинге',
    medium: 'средняя уверенность в рейтинге',
    low: 'низкая уверенность в рейтинге',
  },
  data_quality_hold: {
    high: 'высокая уверенность в статусе данных',
    medium: 'средняя уверенность в статусе данных',
    low: 'низкая уверенность в статусе данных',
  },
};

function formatNumber(value: number): string {
  return new Intl.NumberFormat('ru-RU').format(value);
}

function formatRub(value: number | null): string {
  if (value === null) {
    return '—';
  }
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 0,
  }).format(value);
}

function formatRating(value: number | null): string {
  if (value === null) {
    return '—';
  }
  return value.toLocaleString('ru-RU', { maximumFractionDigits: 2 });
}

function formatPercent(value: number): string {
  return `${formatNumber(value)}%`;
}

function formatSignalCount(value: number, coverage: number): string {
  return coverage === 0 ? 'нет сигнала' : formatNumber(value);
}

function formatSignalNote(coverage: number): string {
  return coverage === 0 ? 'источник не раскрывает поле' : `покрытие ${formatPercent(coverage)}`;
}

function formatTrendDate(value: string): string {
  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  })
    .format(new Date(`${value}T00:00:00.000Z`))
    .replace('.', '');
}

function formatTrendPointCount(value: number): string {
  return formatCountRu(value, ['снимок', 'снимка', 'снимков']);
}

function formatDiscountPercent(value: number): string {
  return `${value.toLocaleString('ru-RU', { maximumFractionDigits: 1 })}%`;
}

function isStaleCollection(collectionDate: string): boolean {
  return collectionDate < new Date().toISOString().slice(0, 10);
}

function jobStatusLabel(status: string): string {
  return JOB_STATUS_LABELS[status] ?? status;
}

function marketplaceLabel(marketplace: Marketplace): string {
  return MARKETPLACE_LABELS[marketplace] ?? marketplace;
}

function categoryLabel(categoryId: string): string {
  return CATEGORY_LABELS[categoryId] ?? categoryId;
}

// The cross-cutting topic focus: when a category is active, every categoryId-bound
// section narrows to it; null means «все темы». Items with a null categoryId (the
// data_quality_hold insight) are global and always match.
function matchesCategory(activeCategory: string | null, categoryId: string | null): boolean {
  return activeCategory === null || categoryId === activeCategory;
}

function reportStatus(dashboard: MarketplaceIntelligenceDashboardPayload): 'complete' | 'partial_with_quality_notes' {
  return dashboard.summary.blockedOrIncidentJobs === 0 && dashboard.summary.completeJobs === dashboard.summary.totalJobs
    ? 'complete'
    : 'partial_with_quality_notes';
}

function maxValue(values: number[]): number {
  return Math.max(...values, 1);
}

// Real h2 in the document outline (hero h1 → section h2 → card h3),
// visually identical to CardTitle: preflight makes headings inherit size/weight.
function SectionHeading({ children }: { children: ReactNode }) {
  return <h2 className="font-semibold leading-none tracking-[-0.02em] text-[var(--site-ink-strong)]">{children}</h2>;
}

function SummaryStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-[color:var(--site-border-soft)] bg-[var(--site-surface)] px-4 py-3">
      <div className="text-xs text-[var(--site-ink-muted)]">{label}</div>
      <div className="mt-1 text-xl font-semibold text-[var(--site-ink)]">{value}</div>
    </div>
  );
}

// Honest placeholder shown when a section is present on the page but has nothing
// under the active topic — the section stays in the DOM so anchors don't break.
function TopicEmptyNote({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-2xl border border-dashed border-[color:var(--site-border-mid)] p-4 text-sm text-[var(--site-ink-muted)]">
      {children}
    </div>
  );
}

function BarMetric({ label, value, max, detail, muted = false }: { label: string; value: number; max: number; detail?: string; muted?: boolean }) {
  const width = `${Math.max((value / max) * 100, value > 0 ? 6 : 0)}%`;
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3 text-sm">
        <span className="font-medium text-[var(--site-ink)]">{label}</span>
        <span className="text-[var(--site-ink-muted)]">{detail ?? formatNumber(value)}</span>
      </div>
      <div className="h-2 rounded-full bg-[var(--site-surface-muted)]">
        <div
          className={muted ? 'h-full rounded-full bg-[var(--site-border-mid)]' : 'h-full rounded-full bg-[var(--site-primary-fill)]'}
          style={{ width }}
        />
      </div>
    </div>
  );
}

function CoverageBadge({ value }: { value: number }) {
  const tone = value >= 90 ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : value >= 60 ? 'border-amber-200 bg-amber-50 text-amber-700' : 'border-rose-200 bg-rose-50 text-rose-700';
  return <span className={`rounded-full border px-2 py-1 text-xs font-medium ${tone}`}>{formatPercent(value)}</span>;
}

function evidenceLabel(insight: MarketplaceDecisionInsight): string {
  const parts = [
    insight.marketplace ? marketplaceLabel(insight.marketplace) : null,
    insight.categoryId ? categoryLabel(insight.categoryId) : null,
    insight.queryText,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : 'Качество данных';
}

function formatMetricSignal(value: number, coverage: number | null): { value: string; note: string | null } {
  if (coverage === 0) {
    return { value: 'нет сигнала', note: 'не раскрыто источником' };
  }
  if (coverage !== null && coverage > 0 && coverage < 60) {
    return { value: formatNumber(value), note: `покрытие ${formatPercent(coverage)}` };
  }
  return { value: formatNumber(value), note: null };
}

function metricRows(insight: MarketplaceDecisionInsight): Array<{ label: string; value: string; note?: string | null }> {
  const metrics = insight.metrics;
  const rows: Array<{ label: string; value: string; note?: string | null }> = [];

  if (metrics.sellerCount !== null) {
    rows.push({ label: 'Продавцы', ...formatMetricSignal(metrics.sellerCount, metrics.sellerCoverage) });
  }
  if (metrics.productCount !== null) {
    rows.push({ label: 'Товары', value: formatNumber(metrics.productCount) });
  }
  if (metrics.brandCount !== null) {
    rows.push({ label: 'Бренды', ...formatMetricSignal(metrics.brandCount, metrics.brandCoverage) });
  }
  if (metrics.medianPriceRub !== null) {
    rows.push({ label: 'Медианная цена', value: formatRub(metrics.medianPriceRub) });
  }
  if (metrics.averageRating !== null) {
    rows.push({ label: 'Средний рейтинг', value: formatRating(metrics.averageRating) });
  }
  if (metrics.priceCoverage !== null) {
    rows.push({ label: 'Покрытие цен', value: formatPercent(metrics.priceCoverage) });
  }
  if (metrics.sellerCoverage !== null) {
    rows.push({ label: 'Покрытие продавцов', value: formatPercent(metrics.sellerCoverage) });
  }
  if (metrics.availabilityCoverage !== null) {
    rows.push({ label: 'Покрытие наличия', value: formatPercent(metrics.availabilityCoverage) });
  }
  if (metrics.affectedJobCount !== null) {
    rows.push({ label: 'Затронуто заданий', value: formatNumber(metrics.affectedJobCount) });
  }

  return rows.slice(0, 6);
}

function localizeInsightCaveat(value: string): string {
  const sellerCoverageMatch = value.match(/^seller coverage (\d+)%$/);
  if (sellerCoverageMatch) {
    return `продавцы раскрыты на ${sellerCoverageMatch[1]}%`;
  }
  const availabilityCoverageMatch = value.match(/^availability coverage (\d+)%$/);
  if (availabilityCoverageMatch) {
    return `наличие раскрыто на ${availabilityCoverageMatch[1]}%`;
  }
  if (value === 'placement split is not exposed by source') {
    return 'источник не раскрывает рекламу и органику отдельно';
  }
  return localizeMarketplaceIntelligenceLimitation(value);
}

function confidenceLabel(insight: MarketplaceDecisionInsight): string {
  return CONFIDENCE_LABELS[insight.decisionType][insight.confidence];
}

// One compact line per decision type. The line must add to, not restate,
// the analyzer's recommendedAction shown right above it.
function decisionUsageLine(insight: MarketplaceDecisionInsight): string {
  switch (insight.decisionType) {
    case 'price_positioning': {
      const median = insight.metrics.medianPriceRub !== null ? `медианой ${formatRub(insight.metrics.medianPriceRub)}` : 'медианой выдачи';
      return `Сравните свою цену с ${median}: выше рынка — проверьте, чем оправдана премия (бренд, комплектность, рейтинг, доставка, контент); ниже — не теряете ли маржу.`;
    }
    case 'monitor_competition':
      return 'Используйте плотность продавцов как сигнал частоты мониторинга: чем плотнее выдача, тем быстрее меняется видимость.';
    case 'strengthen_card':
      return 'Сравните свой рейтинг, отзывы и контент с планкой топа: если карточка слабее выдачи, сначала усиливайте её, потом масштабируйте трафик.';
    case 'data_quality_hold':
      return 'Используйте этот блок как стоп-сигнал: дождитесь полного сбора или проверьте источник перед действием.';
  }
}

function narrativeNoteForInsight(
  dashboard: MarketplaceIntelligenceDashboardPayload,
  insight: MarketplaceDecisionInsight,
): string | null {
  const narrative = dashboard.llmNarrative;
  if (!narrative) {
    return null;
  }
  const action = narrative.recommendedActions.find((item) => item.evidenceIds.includes(insight.evidenceId));
  const section = narrative.sections.find((item) => item.evidenceIds.includes(insight.evidenceId));
  const note = action?.body ?? section?.body ?? null;
  if (!note || note === insight.summary || note === insight.recommendedAction || note === decisionUsageLine(insight)) {
    return null;
  }
  return note;
}

function narrativeNoteForDecisionType(
  dashboard: MarketplaceIntelligenceDashboardPayload,
  decisionType: MarketplaceDecisionType,
): string | null {
  const narrative = dashboard.llmNarrative;
  if (!narrative) {
    return null;
  }
  const evidenceIds = new Set(
    dashboard.decisionInsights
      .filter((insight) => insight.decisionType === decisionType)
      .map((insight) => insight.evidenceId),
  );
  const section = narrative.sections.find((item) => item.evidenceIds.some((evidenceId) => evidenceIds.has(evidenceId)));
  return section?.body ?? null;
}

function DecisionInsightCard({ insight, narrativeNote }: { insight: MarketplaceDecisionInsight; narrativeNote: string | null }) {
  const usageLine = decisionUsageLine(insight);

  return (
    <div className="rounded-3xl border border-[color:var(--site-border-soft)] bg-[var(--site-surface)] p-4 shadow-[var(--site-shadow-soft)] md:p-5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded-full bg-[var(--site-primary-fill)] px-3 py-1 text-xs font-semibold text-[var(--site-primary-text)]">
          {DECISION_TYPE_LABELS[insight.decisionType]}
        </span>
        <span className="rounded-full border border-[color:var(--site-border-soft)] px-3 py-1 text-xs text-[var(--site-ink-muted)]">
          {confidenceLabel(insight)}
        </span>
      </div>
      <div className="mt-4 text-xs font-medium uppercase tracking-wide text-[var(--site-ink-muted)]">{evidenceLabel(insight)}</div>
      <h3 className="mt-2 line-clamp-2 text-lg font-semibold text-[var(--site-ink)] md:line-clamp-none md:text-xl">{insight.title}</h3>
      <p className="mt-3 line-clamp-3 text-sm leading-6 text-[var(--site-ink-muted)] md:line-clamp-none">{insight.summary}</p>
      <div className="mt-4 rounded-2xl bg-[var(--site-surface-muted)] p-3 text-sm text-[var(--site-ink)] md:p-4">
        <div className="text-xs font-semibold uppercase tracking-wide text-[var(--site-ink-muted)]">Решение</div>
        <div className="mt-2 line-clamp-3 md:line-clamp-none">{insight.recommendedAction}</div>
      </div>
      <div className="mt-3 rounded-2xl bg-[var(--site-surface-muted)] px-3 py-2 text-xs leading-5 text-[var(--site-ink-muted)]">
        <span className="font-semibold text-[var(--site-ink)]">Как использовать: </span>{usageLine}
      </div>
      {narrativeNote ? (
        <p className="mt-2 rounded-2xl border border-[color:var(--site-border-soft)] px-3 py-2 text-xs leading-5 text-[var(--site-ink-muted)]">
          {narrativeNote}
        </p>
      ) : null}
      <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
        {metricRows(insight).map((metric) => (
          <div key={metric.label} className="rounded-2xl border border-[color:var(--site-border-soft)] px-2 py-2 md:px-3">
            <div className="text-xs text-[var(--site-ink-muted)]">{metric.label}</div>
            <div className="font-semibold text-[var(--site-ink)]">{metric.value}</div>
            {metric.note ? <div className="mt-1 text-[11px] leading-4 text-[var(--site-ink-muted)]">{metric.note}</div> : null}
          </div>
        ))}
      </div>
      {insight.caveats.length > 0 ? (
        <div className="mt-4 line-clamp-2 text-xs text-[var(--site-ink-muted)] md:line-clamp-none">
          Ограничения: {insight.caveats.slice(0, 3).map(localizeInsightCaveat).join('; ')}
        </div>
      ) : null}
    </div>
  );
}

// The data_quality_hold insight is a warning, not a decision to work through: it
// renders as a compact banner under the action cards instead of an equally heavy card.
function DataQualityHoldBanner({ insight }: { insight: MarketplaceDecisionInsight }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-900">
      <div className="min-w-0">
        <span className="font-semibold">{insight.title}.</span> {insight.recommendedAction}
      </div>
      <a href="#mi-reference" className="shrink-0 font-semibold underline-offset-2 hover:underline">
        Статус источников →
      </a>
    </div>
  );
}

function DecisionMap({ dashboard, activeCategory }: { dashboard: MarketplaceIntelligenceDashboardPayload; activeCategory: string | null }) {
  if (dashboard.decisionInsights.length === 0) {
    return null;
  }

  const actionableInsights = dashboard.decisionInsights
    .filter((insight) => insight.decisionType !== 'data_quality_hold')
    .filter((insight) => matchesCategory(activeCategory, insight.categoryId));
  // The hold is a global stop-signal (categoryId is null), so it survives the topic filter.
  const holdInsights = dashboard.decisionInsights.filter((insight) => insight.decisionType === 'data_quality_hold');

  return (
    <Card id="mi-decisions" className="scroll-mt-52">
      <CardHeader>
        <SectionPill className="self-start">Карта решений</SectionPill>
        <SectionHeading>Где действовать, где следить, где не спешить</SectionHeading>
        <CardDescription>
          Это рабочий список для селлера: каждая карточка показывает действие, причину и минимум метрик для проверки.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {actionableInsights.length > 0 ? (
          <div className="grid gap-4 lg:grid-cols-3">
            {actionableInsights.map((insight) => (
              <DecisionInsightCard key={insight.evidenceId} insight={insight} narrativeNote={narrativeNoteForInsight(dashboard, insight)} />
            ))}
          </div>
        ) : activeCategory ? (
          <TopicEmptyNote>По теме «{categoryLabel(activeCategory)}» нет карточек решений в этом сборе.</TopicEmptyNote>
        ) : null}
        {holdInsights.map((insight) => (
          <DataQualityHoldBanner key={insight.evidenceId} insight={insight} />
        ))}
      </CardContent>
    </Card>
  );
}

type SignificantProductTrend = MarketplaceIntelligenceDashboardPayload['significantProductTrends'][number];

const TREND_SIGNAL_LABELS: Record<SignificantProductTrend['signalType'], string> = {
  price_changed: 'цена резко изменилась',
  position_changed: 'позиция заметно изменилась',
  premium_holds_top: 'премиальная цена держит топ',
};

function formatSignedPercent(value: number | null): string {
  if (value === null) {
    return '—';
  }
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toLocaleString('ru-RU', { maximumFractionDigits: 2 })}%`;
}

function formatRankDelta(value: number): string {
  if (value === 0) {
    return 'без изменения';
  }
  return value > 0 ? `поднялась на ${formatNumber(value)}` : `снизилась на ${formatNumber(Math.abs(value))}`;
}

// Rank movement is unambiguously good/bad, so it gets color; price and market
// deltas stay neutral ink because direction-goodness depends on the reader's side.
function DeltaBadge({ delta, label, colored = false }: { delta: number | null; label: string; colored?: boolean }) {
  if (delta === null) {
    return <div className="mt-1 text-xs font-medium text-[var(--site-ink-muted)]">{label}</div>;
  }
  const arrow = delta > 0 ? '▲' : delta < 0 ? '▼' : '·';
  const tone = !colored || delta === 0
    ? 'text-[var(--site-ink-muted)]'
    : delta > 0
      ? 'text-emerald-700'
      : 'text-rose-700';
  return <div className={`mt-1 text-xs font-medium ${tone}`}>{arrow} {label}</div>;
}

function trendAbsenceSentence(trend: SignificantProductTrend): string {
  if (trend.absences.length === 0) {
    return '';
  }
  return trend.absences.some((absence) => absence.reason === 'not_in_top_n')
    ? ' Часть дней карточки не было в топе выдачи — разрывы показаны пунктиром, даты и причины перечислены выше.'
    : ' Часть дней данные по запросу не собирались — разрывы показаны пунктиром, даты и причины перечислены выше.';
}

function trendSummary(trend: SignificantProductTrend): string {
  let base: string;
  if (trend.signalType === 'premium_holds_top') {
    base = 'Карточка держится в топе при цене выше рынка. Проверьте, чем объясняется премия: бренд, отзывы, комплектация или доставка.';
  } else if (trend.signalType === 'position_changed') {
    base = 'Позиция заметно изменилась. Проверьте, что двигало карточку: цена, рейтинг, наличие или промо.';
  } else {
    base = 'Цена карточки заметно изменилась относительно предыдущего снимка. Проверьте, не началась ли скидка или пересборка ценового коридора.';
  }
  return `${base}${trendAbsenceSentence(trend)}`;
}

function trendDateRange(trend: SignificantProductTrend): string {
  const first = trend.points[0]?.collectionDate;
  const last = trend.points.at(-1)?.collectionDate;
  if (!first || !last) {
    return 'нет дат';
  }
  const dateRange = first === last ? formatTrendDate(first) : `${formatTrendDate(first)} → ${formatTrendDate(last)}`;
  return `${dateRange} · ${formatTrendPointCount(trend.points.length)}`;
}

function trendPointDates(trend: SignificantProductTrend): string {
  return trend.points.map((point) => formatTrendDate(point.collectionDate)).join(', ');
}

const TREND_ABSENCE_REASON_LABELS: Record<SignificantProductTrend['absences'][number]['reason'], string> = {
  not_in_top_n: 'не было в топе выдачи',
  query_not_collected: 'запрос не собирался',
  no_collection: 'сборка не проводилась',
};

function formatTrendDateRange(startDate: string, endDate: string): string {
  if (startDate === endDate) {
    return formatTrendDate(startDate);
  }
  if (startDate.slice(0, 7) === endDate.slice(0, 7)) {
    return `${Number(startDate.slice(8, 10))}–${formatTrendDate(endDate)}`;
  }
  return `${formatTrendDate(startDate)} – ${formatTrendDate(endDate)}`;
}

function trendAbsenceNotes(trend: SignificantProductTrend): string {
  return trend.absences
    .map((absence) => `${formatTrendDateRange(absence.startDate, absence.endDate)} — ${TREND_ABSENCE_REASON_LABELS[absence.reason]}`)
    .join('; ');
}

function formatAxisRub(value: number): string {
  return `${formatNumber(Math.round(value))} ₽`;
}

function ProductTrendChart({ trend }: { trend: SignificantProductTrend }) {
  const geometry = buildTrendChartGeometry({ points: trend.points, marketSeries: trend.marketSeries });
  const { width, height } = TREND_CHART_DIMENSIONS;
  const paddingLeft = TREND_CHART_DIMENSIONS.paddingLeft ?? TREND_CHART_DIMENSIONS.paddingX;
  const paddingRight = TREND_CHART_DIMENSIONS.paddingRight ?? TREND_CHART_DIMENSIONS.paddingX;
  const band = geometry.axes.band;
  const bandLabels = band
    ? Math.abs(band.p25.y - band.p75.y) < 14
      ? [{ key: 'band', y: (band.p25.y + band.p75.y) / 2 + 3, text: `${formatNumber(Math.round(band.p25.valueRub))}–${formatNumber(Math.round(band.p75.valueRub))}` }]
      : [
          { key: 'p75', y: band.p75.y + 3, text: formatNumber(Math.round(band.p75.valueRub)) },
          { key: 'p25', y: band.p25.y + 3, text: formatNumber(Math.round(band.p25.valueRub)) },
        ]
    : [];

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={`Тренд цены: ${trend.title}`}
      className="h-40 w-full overflow-visible rounded-2xl bg-[var(--site-surface-muted)]"
    >
      {geometry.bandPolygon ? <polygon points={geometry.bandPolygon} fill="var(--site-accent-soft)" opacity="0.75" /> : null}
      {geometry.marketLine ? (
        <polyline points={geometry.marketLine} fill="none" stroke="var(--site-ink-muted)" strokeDasharray="5 5" strokeWidth="2" />
      ) : null}
      {geometry.gapConnectors.map((connector) => (
        <polyline
          key={connector}
          points={connector}
          fill="none"
          stroke="var(--site-primary-fill)"
          strokeDasharray="2 6"
          strokeLinecap="round"
          strokeWidth="2"
        />
      ))}
      {geometry.productSegments.map((segment) => (
        <polyline
          key={segment}
          points={segment}
          fill="none"
          stroke="var(--site-primary-fill)"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="4"
        />
      ))}
      {geometry.productDots.map((dot) => (
        <circle key={dot.collectionDate} cx={dot.x} cy={dot.y} r="4" fill="var(--site-primary-fill)" />
      ))}
      {geometry.axes.y.map((tick) => (
        <text key={`y-${tick.valueRub}`} x={paddingLeft - 6} y={tick.y + 3} textAnchor="end" fontSize="10" fill="var(--site-ink-muted)">
          {formatAxisRub(tick.valueRub)}
        </text>
      ))}
      {geometry.axes.x.map((tick) => (
        <text key={`x-${tick.collectionDate}`} x={tick.x} y={height - 6} textAnchor={tick.anchor} fontSize="10" fill="var(--site-ink-muted)">
          {formatTrendDate(tick.collectionDate)}
        </text>
      ))}
      {bandLabels.map((label) => (
        <text key={label.key} x={width - paddingRight + 5} y={label.y} textAnchor="start" fontSize="10" fill="var(--site-ink-muted)">
          {label.text}
        </text>
      ))}
    </svg>
  );
}

function SignificantProductTrends({ dashboard, activeCategory }: { dashboard: MarketplaceIntelligenceDashboardPayload; activeCategory: string | null }) {
  const hasTrends = dashboard.significantProductTrends.length > 0;
  // No trends and not enough history yet → the «Что появится с историей» reference
  // note covers it. But once day-over-day history exists, keep the section (and its
  // anchor) and say plainly that nothing moved this week, instead of silently
  // disappearing — the empty week is a real, honest outcome of the recency window.
  if (!hasTrends && !dashboard.trendAvailability.dayOverDayAvailable) {
    return null;
  }

  const trends = dashboard.significantProductTrends.filter((trend) => matchesCategory(activeCategory, trend.categoryId));

  return (
    <Card id="mi-trends" className="scroll-mt-52">
      <CardHeader>
        <SectionPill className="self-start">История карточек</SectionPill>
        <SectionHeading>Карточки с заметным движением</SectionHeading>
        <CardDescription>
          Заметное движение за последнюю неделю по exact product ID: цена карточки, медиана рынка и ценовой коридор того же запроса. Старые сигналы и одиночные снимки через большой пропуск не показываем.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4 lg:grid-cols-3">
        {!hasTrends ? (
          <div className="lg:col-span-3">
            <TopicEmptyNote>За последнюю неделю карточек с заметным движением не зафиксировано — цены и позиции в наблюдаемых запросах держатся стабильно.</TopicEmptyNote>
          </div>
        ) : activeCategory && trends.length === 0 ? (
          <div className="lg:col-span-3">
            <TopicEmptyNote>В общем топе карточек с заметным движением (он формируется по всему рынку) нет карточек по теме «{categoryLabel(activeCategory)}».</TopicEmptyNote>
          </div>
        ) : null}
        {trends.map((trend) => (
          <article
            key={trend.trendId}
            className="rounded-3xl border border-[color:var(--site-border-soft)] bg-[var(--site-surface)] p-4 shadow-[var(--site-shadow-soft)] md:p-5"
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-[var(--site-primary-fill)] px-3 py-1 text-xs font-semibold text-[var(--site-primary-text)]">
                {TREND_SIGNAL_LABELS[trend.signalType]}
              </span>
              {trend.historyBadge ? (
                <span className="rounded-full border border-[color:var(--site-border-soft)] px-3 py-1 text-xs text-[var(--site-ink-muted)]">
                  {trend.historyBadge}
                </span>
              ) : null}
            </div>
            <div className="mt-4 text-xs font-medium uppercase tracking-wide text-[var(--site-ink-muted)]">
              {marketplaceLabel(trend.marketplace)} · {categoryLabel(trend.categoryId)} · {trend.queryText}
            </div>
            <div className="mt-2 line-clamp-2 text-lg font-semibold text-[var(--site-ink)]">{trend.title}</div>
            <div className="mt-2 rounded-2xl bg-[var(--site-surface-muted)] px-3 py-2 text-xs text-[var(--site-ink-muted)]">
              <span className="font-semibold text-[var(--site-ink)]">Период: </span>{trendDateRange(trend)}
              <span className="mt-1 block">
                <span className="font-semibold text-[var(--site-ink)]">Даты снимков: </span>{trendPointDates(trend)}
              </span>
              {trend.absences.length > 0 ? (
                <span className="mt-1 block">
                  <span className="font-semibold text-[var(--site-ink)]">Без снимков: </span>{trendAbsenceNotes(trend)}
                </span>
              ) : null}
            </div>
            <p className="mt-3 line-clamp-3 text-sm leading-6 text-[var(--site-ink-muted)] md:line-clamp-none">
              {trendSummary(trend)}
            </p>

            <div className="mt-4">
              <ProductTrendChart trend={trend} />
              <div className="mt-2 flex flex-wrap gap-3 text-xs text-[var(--site-ink-muted)]">
                <span><span className="font-semibold text-[var(--site-primary-fill)]">—</span> карточка</span>
                <span><span className="font-semibold text-[var(--site-primary-fill)]">··</span> пропуск в истории</span>
                <span><span className="font-semibold">- -</span> рынок</span>
                <span>зона рынка P25–P75</span>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
              <div className="rounded-2xl border border-[color:var(--site-border-soft)] px-3 py-2">
                <div className="text-xs text-[var(--site-ink-muted)]">Цена за период</div>
                <div className="font-semibold text-[var(--site-ink)]">
                  {trend.points[0] ? `${formatRub(trend.points[0].productPriceRub)} → ` : ''}{formatRub(trend.latestPriceRub)}
                </div>
                <DeltaBadge delta={trend.priceDeltaPct} label={formatSignedPercent(trend.priceDeltaPct)} />
              </div>
              <div className="rounded-2xl border border-[color:var(--site-border-soft)] px-3 py-2">
                <div className="text-xs text-[var(--site-ink-muted)]">Позиция за период</div>
                <div className="font-semibold text-[var(--site-ink)]">
                  {trend.points[0] && trend.points.at(-1) ? `${trend.points[0].rank} → ${trend.points.at(-1)?.rank}` : '—'}
                </div>
                <DeltaBadge delta={trend.rankDelta} label={formatRankDelta(trend.rankDelta)} colored />
              </div>
              <div className="rounded-2xl border border-[color:var(--site-border-soft)] px-3 py-2">
                <div className="text-xs text-[var(--site-ink-muted)]">Медиана рынка</div>
                <div className="font-semibold text-[var(--site-ink)]">
                  {trend.points[0] && trend.points.at(-1)
                    ? `${formatRub(trend.points[0].marketMedianRub)} → ${formatRub(trend.points.at(-1)?.marketMedianRub ?? null)}`
                    : '—'}
                </div>
                <DeltaBadge delta={trend.marketDeltaPct} label={formatSignedPercent(trend.marketDeltaPct)} />
              </div>
              <div className="rounded-2xl border border-[color:var(--site-border-soft)] px-3 py-2">
                <div className="text-xs text-[var(--site-ink-muted)]">К рынку сейчас</div>
                <div className="font-semibold text-[var(--site-ink)]">{formatSignedPercent(trend.latestVsMarketPct)}</div>
                <div className="mt-1 text-xs font-medium text-[var(--site-ink-muted)]">
                  к медиане {formatRub(trend.points.at(-1)?.marketMedianRub ?? null)}
                </div>
              </div>
            </div>

            <a
              href={trend.sourceUrl}
              target="_blank"
              rel="noreferrer"
              aria-label={`Открыть источник: ${trend.title}`}
              className="mt-4 inline-flex min-h-9 items-center rounded-full border border-[color:var(--site-border-soft)] px-3 text-sm font-semibold text-[var(--site-ink)] transition hover:bg-[var(--site-surface-muted)]"
            >
              Открыть источник
            </a>
          </article>
        ))}
      </CardContent>
    </Card>
  );
}

type DailyDeltas = NonNullable<MarketplaceIntelligenceDashboardPayload['dailyDeltas']>;

function dailyDeltaContext(item: { marketplace: Marketplace; categoryId: string; queryText: string }): string {
  return `${marketplaceLabel(item.marketplace)} · ${categoryLabel(item.categoryId)} · ${item.queryText}`;
}

const DAILY_CHANGE_VISIBLE_ROWS = 3;

// Top-3 rows are visible; the tail stays in the server HTML behind a native
// <details> so opening it needs no JS and nothing is lost for search or tests.
function DailyChangeList({ title, children }: { title: string; children: ReactNode }) {
  const rows = React.Children.toArray(children);
  const visibleRows = rows.slice(0, DAILY_CHANGE_VISIBLE_ROWS);
  const hiddenRows = rows.slice(DAILY_CHANGE_VISIBLE_ROWS);

  return (
    <div className="rounded-3xl border border-[color:var(--site-border-soft)] bg-[var(--site-surface)] p-4 shadow-[var(--site-shadow-soft)]">
      <h3 className="text-sm font-semibold text-[var(--site-ink)]">{title}</h3>
      <div className="mt-3 space-y-3">{visibleRows}</div>
      {hiddenRows.length > 0 ? (
        <details className="group mt-3">
          <summary className="cursor-pointer list-none text-xs font-semibold text-[var(--site-ink-muted)] transition hover:text-[var(--site-ink)]">
            <span className="group-open:hidden">Ещё {formatCountRu(hiddenRows.length, ['запись', 'записи', 'записей'])} ↓</span>
            <span className="hidden group-open:inline">Свернуть ↑</span>
          </summary>
          <div className="mt-3 space-y-3">{hiddenRows}</div>
        </details>
      ) : null}
    </div>
  );
}

function DailyChangeRow({ primary, context, value, badge, sourceUrl, sourceTitle }: {
  primary: string;
  context: string;
  value: string;
  badge?: ReactNode;
  sourceUrl?: string;
  sourceTitle?: string;
}) {
  return (
    <div className="border-b border-[color:var(--site-border-soft)] pb-3 text-sm last:border-0 last:pb-0">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="line-clamp-1 font-medium text-[var(--site-ink)]">{primary}</div>
          <div className="mt-0.5 text-xs text-[var(--site-ink-muted)]">{context}</div>
        </div>
        <div className="shrink-0 text-right">
          <div className="font-semibold text-[var(--site-ink)]">{value}</div>
          {badge}
        </div>
      </div>
      {sourceUrl ? (
        <a
          href={sourceUrl}
          target="_blank"
          rel="noreferrer"
          aria-label={`Открыть источник: ${sourceTitle ?? primary}`}
          className="mt-1 inline-flex text-xs font-medium text-[var(--site-ink-muted)] underline-offset-2 hover:underline"
        >
          Открыть источник
        </a>
      ) : null}
    </div>
  );
}

function DailyChangesSection({ dashboard, activeCategory }: { dashboard: MarketplaceIntelligenceDashboardPayload; activeCategory: string | null }) {
  const deltas: DailyDeltas | null = dashboard.dailyDeltas;
  const filtered = deltas
    ? {
        queryMedianShifts: deltas.queryMedianShifts.filter((item) => matchesCategory(activeCategory, item.categoryId)),
        rankJumps: deltas.rankJumps.filter((item) => matchesCategory(activeCategory, item.categoryId)),
        topEntries: deltas.topEntries.filter((item) => matchesCategory(activeCategory, item.categoryId)),
        topExits: deltas.topExits.filter((item) => matchesCategory(activeCategory, item.categoryId)),
        newSellers: deltas.newSellers.filter((item) => matchesCategory(activeCategory, item.categoryId)),
      }
    : null;
  const hasAnyChange = filtered
    ? filtered.queryMedianShifts.length > 0 ||
      filtered.topEntries.length > 0 ||
      filtered.topExits.length > 0 ||
      filtered.rankJumps.length > 0 ||
      filtered.newSellers.length > 0
    : false;

  return (
    <Card id="mi-changes" className="scroll-mt-52">
      <CardHeader>
        <SectionPill className="self-start">Изменения</SectionPill>
        <SectionHeading>Что изменилось с прошлого сбора</SectionHeading>
        {deltas ? (
          <CardDescription>
            Сравнение сборов: {formatRuIsoDate(deltas.baselineDate)} → {formatRuIsoDate(deltas.latestDate)}.
            {deltas.baselineGapDays > 1
              ? ` Между сборами пропуск: ${formatCountRu(deltas.baselineGapDays - 1, ['день', 'дня', 'дней'])} — сравнение идёт с последним доступным сбором.`
              : ' Полные сутки между сборами.'}
          </CardDescription>
        ) : (
          <CardDescription>
            Это первый сбор серии — сравнивать пока не с чем. Изменения появятся после следующего сбора.
          </CardDescription>
        )}
      </CardHeader>
      {deltas && filtered ? (
        <CardContent className="space-y-4">
          {hasAnyChange ? (
            <div className="grid gap-4 lg:grid-cols-2">
              {filtered.queryMedianShifts.length > 0 ? (
                <DailyChangeList title="Сдвиги медианной цены по запросам">
                  {filtered.queryMedianShifts.map((item) => (
                    <DailyChangeRow
                      key={`${item.marketplace}-${item.categoryId}-${item.queryId}`}
                      primary={item.queryText}
                      context={`${marketplaceLabel(item.marketplace)} · ${categoryLabel(item.categoryId)}`}
                      value={`${formatRub(item.previousMedianRub)} → ${formatRub(item.latestMedianRub)}`}
                      badge={<DeltaBadge delta={item.deltaPct} label={formatSignedPercent(item.deltaPct)} />}
                    />
                  ))}
                </DailyChangeList>
              ) : null}
              {filtered.rankJumps.length > 0 ? (
                <DailyChangeList title="Скачки позиций">
                  {filtered.rankJumps.map((item) => (
                    <DailyChangeRow
                      key={`${item.marketplace}-${item.categoryId}-${item.queryId}-${item.sourceProductId}`}
                      primary={item.title}
                      context={dailyDeltaContext(item)}
                      value={`#${item.previousRank} → #${item.latestRank}`}
                      badge={<DeltaBadge delta={item.rankImprovement} label={formatRankDelta(item.rankImprovement)} colored />}
                      sourceUrl={item.sourceUrl}
                      sourceTitle={item.title}
                    />
                  ))}
                </DailyChangeList>
              ) : null}
              {filtered.topEntries.length > 0 ? (
                <DailyChangeList title="Вошли в топ-10 выдачи">
                  {filtered.topEntries.map((item) => (
                    <DailyChangeRow
                      key={`${item.marketplace}-${item.categoryId}-${item.queryId}-${item.sourceProductId}`}
                      primary={item.title}
                      context={dailyDeltaContext(item)}
                      value={`#${item.rank}${item.priceRub !== null ? ` · ${formatRub(item.priceRub)}` : ''}`}
                      sourceUrl={item.sourceUrl}
                      sourceTitle={item.title}
                    />
                  ))}
                </DailyChangeList>
              ) : null}
              {filtered.topExits.length > 0 ? (
                <DailyChangeList title="Выпали из топ-10 выдачи">
                  {filtered.topExits.map((item) => (
                    <DailyChangeRow
                      key={`${item.marketplace}-${item.categoryId}-${item.queryId}-${item.sourceProductId}`}
                      primary={item.title}
                      context={dailyDeltaContext(item)}
                      value={`был #${item.previousRank}`}
                      sourceUrl={item.sourceUrl}
                      sourceTitle={item.title}
                    />
                  ))}
                </DailyChangeList>
              ) : null}
              {filtered.newSellers.length > 0 ? (
                <DailyChangeList title="Новые продавцы в выдаче">
                  {filtered.newSellers.map((item) => (
                    <DailyChangeRow
                      key={`${item.marketplace}-${item.categoryId}-${item.queryId}-${item.sellerName}`}
                      primary={item.sellerName}
                      context={dailyDeltaContext(item)}
                      value=""
                      badge={<div className="line-clamp-1 max-w-44 text-xs text-[var(--site-ink-muted)]">{item.productTitle}</div>}
                      sourceUrl={item.sourceUrl}
                      sourceTitle={item.productTitle}
                    />
                  ))}
                </DailyChangeList>
              ) : null}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-[color:var(--site-border-mid)] p-4 text-sm text-[var(--site-ink-muted)]">
              {activeCategory
                ? `По теме «${categoryLabel(activeCategory)}» существенных изменений между сборами не зафиксировано.`
                : 'Существенных изменений между сборами не зафиксировано: медианы запросов в пределах ±5%, движения позиций меньше 10 мест, состав топ-10 и набор продавцов без новых участников.'}
            </div>
          )}
          {hasAnyChange ? (
            <p className="text-xs leading-5 text-[var(--site-ink-muted)]">
              Детерминированные дельты без участия AI; пороги: медиана ±5%, позиция ±10, граница топ-10.
            </p>
          ) : null}
        </CardContent>
      ) : null}
    </Card>
  );
}

function ChartCard({ title, description, usage, narrativeNote, children }: { title: string; description: string; usage: string; narrativeNote?: string | null; children: ReactNode }) {
  return (
    <div className="rounded-3xl border border-[color:var(--site-border-soft)] bg-[var(--site-surface)] p-5 shadow-[var(--site-shadow-soft)]">
      <h3 className="text-lg font-semibold text-[var(--site-ink)]">{title}</h3>
      <p className="mt-2 text-sm leading-6 text-[var(--site-ink-muted)]">{description}</p>
      <div className="mt-3 rounded-2xl bg-[var(--site-surface-muted)] px-3 py-2 text-xs leading-5 text-[var(--site-ink-muted)]">
        <span className="font-semibold text-[var(--site-ink)]">Как использовать: </span>{usage}
        {narrativeNote ? <span className="mt-1 block">{narrativeNote}</span> : null}
      </div>
      <div className="mt-5 space-y-4">{children}</div>
    </div>
  );
}

function buildPriceCheckOptions(dashboard: MarketplaceIntelligenceDashboardPayload, activeCategory: string | null): PriceCheckOption[] {
  return [...dashboard.querySummaries]
    .filter((summary) => summary.medianPriceRub !== null)
    .filter((summary) => matchesCategory(activeCategory, summary.categoryId))
    .sort((left, right) =>
      left.marketplace.localeCompare(right.marketplace) ||
      left.queryText.localeCompare(right.queryText),
    )
    .map((summary) => ({
      id: `${summary.marketplace}:${summary.categoryId}:${summary.queryId}`,
      group: marketplaceLabel(summary.marketplace),
      label: `${summary.queryText} · ${categoryLabel(summary.categoryId)}`,
      medianPriceRub: summary.medianPriceRub as number,
      p25PriceRub: summary.p25PriceRub,
      p75PriceRub: summary.p75PriceRub,
      snapshotCount: summary.snapshotCount,
      priceCoveragePercent: summary.fieldCoverage.currentPrice,
    }));
}

function PriceCheckSection({ dashboard, activeCategory }: { dashboard: MarketplaceIntelligenceDashboardPayload; activeCategory: string | null }) {
  // Globally empty → section is absent from the anchor nav too, so return null.
  if (buildPriceCheckOptions(dashboard, null).length === 0) {
    return null;
  }
  const options = buildPriceCheckOptions(dashboard, activeCategory);

  return (
    <Card id="mi-price-check" className="scroll-mt-52">
      <CardHeader>
        <SectionPill className="self-start">Моя цена</SectionPill>
        <SectionHeading>Проверьте свою цену</SectionHeading>
        <CardDescription>
          Выберите запрос и введите свою цену — увидите её положение в ценовом коридоре выдачи P25–P75.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {options.length > 0 ? (
          <PriceCheckWidget options={options} collectionDateLabel={formatRuIsoDate(dashboard.collection.collectionDate)} />
        ) : activeCategory ? (
          <TopicEmptyNote>По теме «{categoryLabel(activeCategory)}» нет запросов с медианной ценой для проверки.</TopicEmptyNote>
        ) : null}
      </CardContent>
    </Card>
  );
}

function buildChartRows(summaries: QuerySummary[]) {
  const competitionRows = [...summaries]
    .filter((summary) => summary.uniqueSellers > 0 && summary.fieldCoverage.sellerName > 0)
    .sort((left, right) => right.uniqueSellers - left.uniqueSellers || right.snapshotCount - left.snapshotCount)
    .slice(0, 5);
  const priceRows = [...summaries]
    .filter((summary) => summary.medianPriceRub !== null)
    .sort((left, right) => (right.medianPriceRub ?? 0) - (left.medianPriceRub ?? 0))
    .slice(0, 5);
  const ratingRows = [...summaries]
    .filter((summary) => summary.averageRating !== null)
    .sort((left, right) => (right.averageRating ?? 0) - (left.averageRating ?? 0))
    .slice(0, 5);
  return {
    competitionRows,
    priceRows,
    ratingRows,
    hasAny: competitionRows.length > 0 || priceRows.length > 0 || ratingRows.length > 0,
  };
}

function DecisionEvidenceCharts({ dashboard, activeCategory }: { dashboard: MarketplaceIntelligenceDashboardPayload; activeCategory: string | null }) {
  // Globally empty → section is absent from the anchor nav too, so return null.
  if (!buildChartRows(dashboard.querySummaries).hasAny) {
    return null;
  }
  const scoped = activeCategory
    ? dashboard.querySummaries.filter((summary) => summary.categoryId === activeCategory)
    : dashboard.querySummaries;
  const { competitionRows, priceRows, ratingRows, hasAny } = buildChartRows(scoped);
  const competitionMax = maxValue(competitionRows.map((summary) => summary.uniqueSellers));
  const priceMax = maxValue(priceRows.map((summary) => summary.medianPriceRub ?? 0));

  return (
    <Card id="mi-charts" className="scroll-mt-52">
      <CardHeader>
        <SectionPill className="self-start">Графики для решения</SectionPill>
        <SectionHeading>Агрегированный срез без таблиц</SectionHeading>
        <CardDescription>
          Простые графики отвечают на главные вопросы: где конкуренция плотнее, где выше ценовой коридор и где выше планка качества карточки.
        </CardDescription>
      </CardHeader>
      {hasAny ? (
      <CardContent className="grid gap-4 lg:grid-cols-3">
        {competitionRows.length > 0 ? (
          <ChartCard
            title="Где плотнее конкуренция"
            description="Больше продавцов в выдаче — выше риск потерять видимость без контроля цены, наличия и карточки."
            usage="Высокая конкуренция означает, что цену, наличие, рейтинг и промо нужно мониторить чаще."
            narrativeNote={narrativeNoteForDecisionType(dashboard, 'monitor_competition')}
          >
            {competitionRows.map((summary) => (
              <BarMetric
                key={`${summary.marketplace}-${summary.categoryId}-${summary.queryId}`}
                label={`${marketplaceLabel(summary.marketplace)} · ${summary.queryText}`}
                value={summary.uniqueSellers}
                max={competitionMax}
                detail={formatCountRu(summary.uniqueSellers, ['продавец', 'продавца', 'продавцов'])}
              />
            ))}
          </ChartCard>
        ) : null}
        {priceRows.length > 0 ? (
          <ChartCard
            title="Где выше ценовой коридор"
            description="Медианная цена помогает понять, где проверять премиальность, скидку или ценностное сообщение."
            usage="Сравните свою цену с медианой: выше рынка — проверьте ценностное объяснение, ниже рынка — проверьте маржу."
            narrativeNote={narrativeNoteForDecisionType(dashboard, 'price_positioning')}
          >
            {priceRows.map((summary) => (
              <BarMetric
                key={`${summary.marketplace}-${summary.categoryId}-${summary.queryId}`}
                label={`${marketplaceLabel(summary.marketplace)} · ${summary.queryText}`}
                value={summary.medianPriceRub ?? 0}
                max={priceMax}
                detail={formatRub(summary.medianPriceRub)}
              />
            ))}
          </ChartCard>
        ) : null}
        {ratingRows.length > 0 ? (
          <ChartCard
            title="Где выше планка рейтинга"
            description="Высокий средний рейтинг в топе значит: слабую карточку нельзя компенсировать только трафиком."
            usage="Если ваш рейтинг ниже планки топа, сначала усиливайте карточку, отзывы и доставку, а потом увеличивайте трафик."
            narrativeNote={narrativeNoteForDecisionType(dashboard, 'strengthen_card')}
          >
            {ratingRows.map((summary) => (
              <BarMetric
                key={`${summary.marketplace}-${summary.categoryId}-${summary.queryId}`}
                label={`${marketplaceLabel(summary.marketplace)} · ${summary.queryText}`}
                value={summary.averageRating ?? 0}
                max={5}
                detail={`★ ${formatRating(summary.averageRating)}`}
              />
            ))}
          </ChartCard>
        ) : null}
      </CardContent>
      ) : (
        <CardContent>
          {activeCategory ? (
            <TopicEmptyNote>По теме «{categoryLabel(activeCategory)}» нет данных для графиков в этом сборе.</TopicEmptyNote>
          ) : null}
        </CardContent>
      )}
    </Card>
  );
}

function StaleReportNotice({ dashboard }: { dashboard: MarketplaceIntelligenceDashboardPayload }) {
  const stale = isStaleCollection(dashboard.collection.collectionDate);

  if (!stale) {
    return null;
  }

  return (
    <Card className="border-amber-200 bg-amber-50">
      <CardContent className="py-4 text-sm text-amber-800">
        Последний валидный отчёт опубликован за {formatRuIsoDate(dashboard.collection.collectionDate)}. Новая публикация появится только после полного прохождения проверки данных и AI-выводов.
      </CardContent>
    </Card>
  );
}

function DetailsSection({ title, description, children }: { title: string; description: string; children: ReactNode }) {
  return (
    <details className="group rounded-3xl border border-[color:var(--site-border-soft)] bg-[var(--site-surface)] shadow-[var(--site-shadow-soft)]">
      <summary className="flex cursor-pointer list-none items-start justify-between gap-4 px-5 py-4 marker:hidden">
        <span>
          <h3 className="text-lg font-semibold text-[var(--site-ink)]">{title}</h3>
          <span className="mt-1 block text-sm text-[var(--site-ink-muted)]">{description}</span>
        </span>
        <span className="rounded-full border border-[color:var(--site-border-soft)] px-3 py-1 text-sm text-[var(--site-ink-muted)] group-open:hidden">Открыть</span>
        <span className="hidden rounded-full border border-[color:var(--site-border-soft)] px-3 py-1 text-sm text-[var(--site-ink-muted)] group-open:inline">Скрыть</span>
      </summary>
      <div className="border-t border-[color:var(--site-border-soft)] p-4 md:p-5">{children}</div>
    </details>
  );
}

function MethodologyNote({ dashboard }: { dashboard: MarketplaceIntelligenceDashboardPayload }) {
  const status = reportStatus(dashboard);
  const generationStatus = dashboard.generationStatus;

  return (
    <Card className="border-[color:var(--site-accent-border)] bg-[var(--site-accent-soft)]">
      <CardHeader>
        <div className="flex flex-wrap items-center gap-2">
          <SectionPill>Методология</SectionPill>
          <span className="rounded-full border border-[color:var(--site-border-soft)] bg-[var(--site-surface)] px-3 py-1 text-xs font-medium text-[var(--site-ink-muted)]">
            {STATUS_LABELS[status]}
          </span>
          <span className="rounded-full border border-[color:var(--site-border-soft)] bg-[var(--site-surface)] px-3 py-1 text-xs font-medium text-[var(--site-ink-muted)]">
            без синтетических строк
          </span>
        </div>
        <CardTitle>Как читать этот Пульс маркетплейсов</CardTitle>
        <CardDescription>
          Ранги и метрики считаются в контексте площадки, категории, запроса, региона и даты сбора. Персонализация выдачи маркетплейсов и скрытые источником поля могут отличать цифры отчёта от кабинета конкретного селлера.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4 text-sm text-[var(--site-ink-muted)] md:grid-cols-4">
        <div>
          <div className="font-semibold text-[var(--site-ink)]">Дата и регион</div>
          <div>{formatRuIsoDate(dashboard.collection.collectionDate)} · {dashboard.collection.collectionRegionLabel}</div>
          <div>Сформировано: {formatMoscowDateTime(dashboard.collection.generatedAt)}</div>
        </div>
        <div>
          <div className="font-semibold text-[var(--site-ink)]">Глубина данных</div>
          <div>{formatCountRu(dashboard.summary.totalSnapshots, ['карточка', 'карточки', 'карточек'])} из {formatCountRu(dashboard.summary.totalJobs, ['задания', 'заданий', 'заданий'])} сбора.</div>
          <div>{formatNumber(dashboard.summary.completeJobs)} завершено, {formatNumber(dashboard.summary.blockedOrIncidentJobs)} с ограничениями.</div>
        </div>
        <div>
          <div className="font-semibold text-[var(--site-ink)]">История</div>
          {dashboard.trendAvailability.dayOverDayAvailable ? (
            <>
              <div>
                {formatCountRu(dashboard.trendAvailability.collectionDateCount, ['дата', 'даты', 'дат'])} сбора:{' '}
                {formatRuIsoDate(dashboard.trendAvailability.firstCollectionDate)} — {formatRuIsoDate(dashboard.trendAvailability.lastCollectionDate)}.
              </div>
              <div>История — до 14 дат сбора; «заметное движение» считается за последнюю неделю.</div>
            </>
          ) : (
            <>
              <div>первая дата сбора в этой серии</div>
              <div>История и тренды накапливаются со следующих сборов.</div>
            </>
          )}
        </div>
        <div>
          <div className="font-semibold text-[var(--site-ink)]">AI-пояснения</div>
          {generationStatus ? (
            <>
              <div>Сгенерировано: {formatMoscowDateTime(generationStatus.generatedAt)}</div>
              <div>{generationStatus.model} · {generationStatus.validationStatus}</div>
            </>
          ) : (
            <div>пояснения не опубликованы</div>
          )}
          <div>Текст не добавляет новые цифры; рядом показаны детерминированные метрики.</div>
        </div>
      </CardContent>
    </Card>
  );
}

function MarketplaceComparison({ dashboard, activeCategory }: { dashboard: MarketplaceIntelligenceDashboardPayload; activeCategory: string | null }) {
  const productMax = maxValue(dashboard.marketplaceSummaries.map((summary) => summary.uniqueProducts));
  const sellerMax = maxValue(dashboard.marketplaceSummaries.map((summary) => summary.uniqueSellers));
  const brandMax = maxValue(dashboard.marketplaceSummaries.map((summary) => summary.uniqueBrands));

  return (
    <Card id="mi-comparison" className="scroll-mt-52">
      <CardHeader>
        <SectionPill className="self-start">Сравнение</SectionPill>
        <SectionHeading>Сравнение маркетплейсов</SectionHeading>
        <CardDescription>
          Быстрый срез по плотности карточек, продавцов, брендов, ценам и покрытию полей.
          {activeCategory ? ' Этот срез агрегируется по площадкам и остаётся по всем темам.' : ''}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          <SummaryStat label="Карточки в наблюдении" value={formatNumber(dashboard.summary.totalSnapshots)} />
          <SummaryStat label="Уникальные товары" value={formatNumber(dashboard.summary.uniqueProducts)} />
          <SummaryStat label="Медианная цена рынка" value={formatRub(dashboard.summary.medianPriceRub)} />
          <SummaryStat label="Средний рейтинг" value={formatRating(dashboard.summary.averageRating)} />
        </div>
        <div className="grid gap-5 lg:grid-cols-3">
          {dashboard.marketplaceSummaries.map((summary) => (
            <div key={summary.marketplace} className="rounded-2xl border border-[color:var(--site-border-soft)] bg-[var(--site-surface)] p-4 shadow-[var(--site-shadow-soft)]">
              <div className="mb-4 flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-[var(--site-ink)]">{marketplaceLabel(summary.marketplace)}</h3>
                  <p className="text-sm text-[var(--site-ink-muted)]">{formatNumber(summary.snapshotCount)} карточек · медиана {formatRub(summary.medianPriceRub)}</p>
                </div>
                <span className="rounded-full bg-[var(--site-surface-muted)] px-2 py-1 text-xs text-[var(--site-ink-muted)]">★ {formatRating(summary.averageRating)}</span>
              </div>
              <div className="space-y-4">
                <BarMetric label="Товары" value={summary.uniqueProducts} max={productMax} />
                <BarMetric
                  label="Продавцы"
                  value={summary.uniqueSellers}
                  max={sellerMax}
                  detail={formatSignalCount(summary.uniqueSellers, summary.fieldCoverage.sellerName)}
                  muted={summary.fieldCoverage.sellerName === 0}
                />
                <BarMetric
                  label="Бренды"
                  value={summary.uniqueBrands}
                  max={brandMax}
                  detail={formatSignalCount(summary.uniqueBrands, summary.fieldCoverage.brandName)}
                  muted={summary.fieldCoverage.brandName === 0}
                />
                <div className="grid grid-cols-2 gap-2 pt-2 text-xs">
                  <div className="flex items-center justify-between gap-2 rounded-xl bg-[var(--site-surface-muted)] px-3 py-2"><span>Бренд</span><CoverageBadge value={summary.fieldCoverage.brandName} /></div>
                  <div className="flex items-center justify-between gap-2 rounded-xl bg-[var(--site-surface-muted)] px-3 py-2"><span>Продавец</span><CoverageBadge value={summary.fieldCoverage.sellerName} /></div>
                  <div className="flex items-center justify-between gap-2 rounded-xl bg-[var(--site-surface-muted)] px-3 py-2"><span>Цена</span><CoverageBadge value={summary.fieldCoverage.currentPrice} /></div>
                  <div className="flex items-center justify-between gap-2 rounded-xl bg-[var(--site-surface-muted)] px-3 py-2"><span>Наличие</span><CoverageBadge value={summary.fieldCoverage.availabilityStatus} /></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

type QuerySummary = MarketplaceIntelligenceDashboardPayload['querySummaries'][number];

type TopicAggregate = {
  categoryId: string;
  label: string;
  queryCount: number;
  marketplaceCount: number;
  priceMinRub: number | null;
  priceMaxRub: number | null;
  coveragePrice: number;
  coverageSellers: number;
  coverageAvailability: number;
  hasIncident: boolean;
  hasData: boolean;
  summaries: QuerySummary[];
};

// Coverage is weighted by snapshot count so a thin job can't swing the topic
// average; an all-zero field stays 0 → rendered as «нет сигнала», never a fake 0.
function weightedCoverage(summaries: QuerySummary[], field: keyof QuerySummary['fieldCoverage']): number {
  const totalSnapshots = summaries.reduce((sum, summary) => sum + summary.snapshotCount, 0);
  if (totalSnapshots === 0) {
    return 0;
  }
  const weighted = summaries.reduce((sum, summary) => sum + summary.fieldCoverage[field] * summary.snapshotCount, 0);
  return Math.round(weighted / totalSnapshots);
}

function aggregateTopics(dashboard: MarketplaceIntelligenceDashboardPayload): TopicAggregate[] {
  const byCategory = new Map<string, QuerySummary[]>();
  for (const summary of dashboard.querySummaries) {
    const bucket = byCategory.get(summary.categoryId);
    if (bucket) {
      bucket.push(summary);
    } else {
      byCategory.set(summary.categoryId, [summary]);
    }
  }
  // A category whose every job was blocked produces no snapshots and would vanish
  // from this section; seed category metadata from jobStatuses too, so it renders
  // an honest «данные не собраны» card instead of silently disappearing.
  const jobsByCategory = new Map<string, typeof dashboard.jobStatuses>();
  for (const job of dashboard.jobStatuses) {
    const bucket = jobsByCategory.get(job.categoryId);
    if (bucket) {
      bucket.push(job);
    } else {
      jobsByCategory.set(job.categoryId, [job]);
    }
  }
  const incidentCategories = new Set(
    dashboard.jobStatuses.filter((job) => job.status !== 'complete').map((job) => job.categoryId),
  );
  const categoryIds = new Set<string>([...byCategory.keys(), ...jobsByCategory.keys()]);

  return [...categoryIds]
    .map((categoryId) => {
      const summaries = byCategory.get(categoryId) ?? [];
      const jobs = jobsByCategory.get(categoryId) ?? [];
      const hasData = summaries.length > 0;
      const medians = summaries
        .map((summary) => summary.medianPriceRub)
        .filter((value): value is number => value !== null);
      return {
        categoryId,
        label: categoryLabel(categoryId),
        queryCount: new Set((hasData ? summaries.map((summary) => summary.queryId) : jobs.map((job) => job.queryId))).size,
        marketplaceCount: new Set((hasData ? summaries.map((summary) => summary.marketplace) : jobs.map((job) => job.marketplace))).size,
        priceMinRub: medians.length > 0 ? Math.min(...medians) : null,
        priceMaxRub: medians.length > 0 ? Math.max(...medians) : null,
        coveragePrice: weightedCoverage(summaries, 'currentPrice'),
        coverageSellers: weightedCoverage(summaries, 'sellerName'),
        coverageAvailability: weightedCoverage(summaries, 'availabilityStatus'),
        hasIncident: incidentCategories.has(categoryId),
        hasData,
        summaries,
      };
    })
    .sort((left, right) => left.label.localeCompare(right.label, 'ru'));
}

function topicPriceRange(topic: TopicAggregate): string {
  if (topic.priceMinRub === null || topic.priceMaxRub === null) {
    return 'нет цен';
  }
  if (topic.priceMinRub === topic.priceMaxRub) {
    return formatRub(topic.priceMinRub);
  }
  return `${formatRub(topic.priceMinRub)} – ${formatRub(topic.priceMaxRub)}`;
}

function CoverageChip({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-xl bg-[var(--site-surface-muted)] px-3 py-2 text-xs">
      <span className="text-[var(--site-ink-muted)]">{label}</span>
      {value === 0 ? (
        <span className="rounded-full border border-[color:var(--site-border-soft)] px-2 py-0.5 text-[var(--site-ink-muted)]">нет сигнала</span>
      ) : (
        <CoverageBadge value={value} />
      )}
    </div>
  );
}

const TOPIC_MARKETPLACE_ORDER: Marketplace[] = ['ozon', 'wildberries', 'yandex_market'];

function TopicQueryRow({ summary }: { summary: QuerySummary }) {
  return (
    <div className="rounded-2xl border border-[color:var(--site-border-soft)] px-3 py-2">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs font-medium uppercase tracking-wide text-[var(--site-ink-muted)]">{marketplaceLabel(summary.marketplace)}</div>
          <div className="truncate text-sm font-medium text-[var(--site-ink)]">{summary.queryText}</div>
        </div>
        <div className="shrink-0 text-right text-sm">
          <div className="font-semibold text-[var(--site-ink)]">{formatRub(summary.medianPriceRub)}</div>
          <div className="text-xs text-[var(--site-ink-muted)]">лучшая позиция №{summary.bestRank}</div>
        </div>
      </div>
      <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-[var(--site-ink-muted)]">
        <span>Цена: {summary.fieldCoverage.currentPrice === 0 ? 'нет сигнала' : formatPercent(summary.fieldCoverage.currentPrice)}</span>
        <span>Продавцы: {summary.fieldCoverage.sellerName === 0 ? 'нет сигнала' : formatPercent(summary.fieldCoverage.sellerName)}</span>
        <span>Наличие: {summary.fieldCoverage.availabilityStatus === 0 ? 'нет сигнала' : formatPercent(summary.fieldCoverage.availabilityStatus)}</span>
      </div>
    </div>
  );
}

function TopicCoverageSection({ dashboard, activeCategory }: { dashboard: MarketplaceIntelligenceDashboardPayload; activeCategory: string | null }) {
  const topics = useMemo(() => aggregateTopics(dashboard), [dashboard]);
  const [openCategoryId, setOpenCategoryId] = useState<string | null>(null);

  if (topics.length === 0) {
    return null;
  }

  const visibleTopics = activeCategory ? topics.filter((topic) => topic.categoryId === activeCategory) : topics;

  return (
    <Card id="mi-topics" className="scroll-mt-52">
      <CardHeader>
        <SectionPill className="self-start">Темы</SectionPill>
        <SectionHeading>Темы и покрытие данных</SectionHeading>
        <CardDescription>
          Выберите тематику — увидите её запросы по всем площадкам и насколько полно собраны данные.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {visibleTopics.map((topic) => {
            const open = topic.hasData && openCategoryId === topic.categoryId;
            const panelId = `mi-topic-${topic.categoryId}`;
            const orderedSummaries = [...topic.summaries].sort(
              (left, right) =>
                TOPIC_MARKETPLACE_ORDER.indexOf(left.marketplace) - TOPIC_MARKETPLACE_ORDER.indexOf(right.marketplace) ||
                left.queryText.localeCompare(right.queryText),
            );
            return (
              <div
                key={topic.categoryId}
                className="rounded-3xl border border-[color:var(--site-border-soft)] bg-[var(--site-surface)] p-4 shadow-[var(--site-shadow-soft)]"
              >
                <button
                  type="button"
                  onClick={topic.hasData ? () => setOpenCategoryId(open ? null : topic.categoryId) : undefined}
                  disabled={!topic.hasData}
                  aria-expanded={topic.hasData ? open : undefined}
                  aria-controls={topic.hasData ? panelId : undefined}
                  className="flex w-full items-start justify-between gap-3 text-left disabled:cursor-default"
                >
                  <span className="min-w-0">
                    <span className="block text-base font-semibold text-[var(--site-ink)]">{topic.label}</span>
                    <span className="mt-0.5 block text-xs text-[var(--site-ink-muted)]">
                      {formatCountRu(topic.queryCount, ['запрос', 'запроса', 'запросов'])} · {formatCountRu(topic.marketplaceCount, ['площадка', 'площадки', 'площадок'])}
                    </span>
                    {topic.hasIncident ? (
                      <span className="mt-1 block text-xs font-medium text-amber-700">⚠ есть несобранный запрос</span>
                    ) : null}
                  </span>
                  <span className="shrink-0 text-right">
                    <span className="block text-xs text-[var(--site-ink-muted)]">медиана</span>
                    <span className="block text-sm font-semibold text-[var(--site-ink)]">{topic.hasData ? topicPriceRange(topic) : '—'}</span>
                    <span className="mt-1 block text-xs text-[var(--site-ink-muted)]">{topic.hasData ? (open ? 'Скрыть ↑' : 'Запросы ↓') : 'нет данных'}</span>
                  </span>
                </button>
                {topic.hasData ? (
                  <div className="mt-3 grid grid-cols-3 gap-2">
                    <CoverageChip label="Цена" value={topic.coveragePrice} />
                    <CoverageChip label="Продавцы" value={topic.coverageSellers} />
                    <CoverageChip label="Наличие" value={topic.coverageAvailability} />
                  </div>
                ) : (
                  <div className="mt-3 rounded-xl bg-[var(--site-surface-muted)] px-3 py-2 text-xs text-[var(--site-ink-muted)]">
                    Данные за этот сбор не собраны.{' '}
                    <a href="#mi-reference" className="font-medium text-[var(--site-ink)] underline-offset-2 hover:underline">Статус источников →</a>
                  </div>
                )}
                {open ? (
                  <div id={panelId} className="mt-3 space-y-2 border-t border-[color:var(--site-border-soft)] pt-3">
                    {orderedSummaries.map((summary) => (
                      <TopicQueryRow key={`${summary.marketplace}-${summary.categoryId}-${summary.queryId}`} summary={summary} />
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function QueryMatrix({ dashboard }: { dashboard: MarketplaceIntelligenceDashboardPayload }) {
  const [marketplaceFilter, setMarketplaceFilter] = useState<FilterValue<Marketplace>>('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [visibleCount, setVisibleCount] = useState(18);

  const categoryOptions = useMemo(() => {
    return [...new Set(dashboard.querySummaries.map((summary) => summary.categoryId))].sort();
  }, [dashboard.querySummaries]);

  const queryRows = useMemo(() => {
    return dashboard.querySummaries
      .filter((summary) => marketplaceFilter === 'all' || summary.marketplace === marketplaceFilter)
      .filter((summary) => categoryFilter === 'all' || summary.categoryId === categoryFilter)
      .sort((left, right) =>
        right.uniqueSellers - left.uniqueSellers ||
        right.snapshotCount - left.snapshotCount ||
        left.categoryId.localeCompare(right.categoryId) ||
        left.queryText.localeCompare(right.queryText),
      );
  }, [categoryFilter, dashboard.querySummaries, marketplaceFilter]);

  const visibleRows = queryRows.slice(0, visibleCount);

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <CardTitle>Категории и запросы</CardTitle>
            <CardDescription>Матрица показывает, где больше продавцов, брендов и карточек в топовой выдаче.</CardDescription>
          </div>
          <div className="flex flex-wrap gap-3">
            <select
              className="min-h-10 rounded-xl border border-[color:var(--site-border-soft)] bg-[var(--site-surface)] px-3 text-sm"
              value={marketplaceFilter}
              onChange={(event) => {
                setMarketplaceFilter(event.target.value as FilterValue<Marketplace>);
                setVisibleCount(18);
              }}
              aria-label="Фильтр маркетплейса"
            >
              <option value="all">Все площадки</option>
              {Object.entries(MARKETPLACE_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
            <select
              className="min-h-10 rounded-xl border border-[color:var(--site-border-soft)] bg-[var(--site-surface)] px-3 text-sm"
              value={categoryFilter}
              onChange={(event) => {
                setCategoryFilter(event.target.value);
                setVisibleCount(18);
              }}
              aria-label="Фильтр категории"
            >
              <option value="all">Все категории</option>
              {categoryOptions.map((categoryId) => <option key={categoryId} value={categoryId}>{categoryLabel(categoryId)}</option>)}
            </select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {visibleRows.map((summary) => (
            <div key={`${summary.marketplace}-${summary.categoryId}-${summary.queryId}`} className="rounded-2xl border border-[color:var(--site-border-soft)] bg-[var(--site-surface)] p-4">
              <div className="mb-3 flex items-start justify-between gap-3">
                <div>
                  <div className="text-xs font-medium uppercase tracking-wide text-[var(--site-ink-muted)]">{marketplaceLabel(summary.marketplace)} · {categoryLabel(summary.categoryId)}</div>
                  <div className="mt-1 font-semibold text-[var(--site-ink)]">{summary.queryText}</div>
                </div>
                <span className="rounded-full bg-[var(--site-surface-muted)] px-2 py-1 text-xs text-[var(--site-ink-muted)]">лучшая позиция №{summary.bestRank}</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="rounded-xl bg-[var(--site-surface-muted)] px-3 py-2"><div className="text-[var(--site-ink-muted)]">Карточки</div><div className="font-semibold">{formatNumber(summary.snapshotCount)}</div></div>
                <div className="rounded-xl bg-[var(--site-surface-muted)] px-3 py-2">
                  <div className="text-[var(--site-ink-muted)]">Продавцы</div>
                  <div className="font-semibold">{formatSignalCount(summary.uniqueSellers, summary.fieldCoverage.sellerName)}</div>
                  <div className="mt-1 text-xs text-[var(--site-ink-muted)]">{formatSignalNote(summary.fieldCoverage.sellerName)}</div>
                </div>
                <div className="rounded-xl bg-[var(--site-surface-muted)] px-3 py-2">
                  <div className="text-[var(--site-ink-muted)]">Бренды</div>
                  <div className="font-semibold">{formatSignalCount(summary.uniqueBrands, summary.fieldCoverage.brandName)}</div>
                  <div className="mt-1 text-xs text-[var(--site-ink-muted)]">{formatSignalNote(summary.fieldCoverage.brandName)}</div>
                </div>
                <div className="rounded-xl bg-[var(--site-surface-muted)] px-3 py-2"><div className="text-[var(--site-ink-muted)]">Медиана</div><div className="font-semibold">{formatRub(summary.medianPriceRub)}</div></div>
              </div>
            </div>
          ))}
        </div>
        {visibleRows.length < queryRows.length ? (
          <div className="mt-5 flex justify-center">
            <Button type="button" variant="outline" onClick={() => setVisibleCount((count) => count + 18)}>
              Показать ещё ({formatNumber(queryRows.length - visibleRows.length)})
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function TopProducts({ dashboard }: { dashboard: MarketplaceIntelligenceDashboardPayload }) {
  const [marketplaceFilter, setMarketplaceFilter] = useState<FilterValue<Marketplace>>('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [visibleProductCount, setVisibleProductCount] = useState(12);

  const categoryOptions = useMemo(() => {
    return [...new Set(dashboard.topProducts.map((product) => product.categoryId))].sort();
  }, [dashboard.topProducts]);

  const products = useMemo(() => {
    return dashboard.topProducts
      .filter((product) => marketplaceFilter === 'all' || product.marketplace === marketplaceFilter)
      .filter((product) => categoryFilter === 'all' || product.categoryId === categoryFilter);
  }, [categoryFilter, dashboard.topProducts, marketplaceFilter]);

  const visibleProducts = products.slice(0, visibleProductCount);

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <CardTitle>Товары в топе</CardTitle>
            <CardDescription>Кураторская выборка карточек из последнего проверенного сбора: достаточно конкретики без публикации всего датасета.</CardDescription>
          </div>
          <div className="flex flex-wrap gap-3">
            <select
              className="min-h-10 rounded-xl border border-[color:var(--site-border-soft)] bg-[var(--site-surface)] px-3 text-sm"
              value={marketplaceFilter}
              onChange={(event) => setMarketplaceFilter(event.target.value as FilterValue<Marketplace>)}
              aria-label="Фильтр товаров по маркетплейсу"
            >
              <option value="all">Все площадки</option>
              {Object.entries(MARKETPLACE_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
            <select
              className="min-h-10 rounded-xl border border-[color:var(--site-border-soft)] bg-[var(--site-surface)] px-3 text-sm"
              value={categoryFilter}
              onChange={(event) => setCategoryFilter(event.target.value)}
              aria-label="Фильтр товаров по категории"
            >
              <option value="all">Все категории</option>
              {categoryOptions.map((categoryId) => <option key={categoryId} value={categoryId}>{categoryLabel(categoryId)}</option>)}
            </select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid gap-3 lg:grid-cols-2">
          {visibleProducts.map((product) => (
            <article
              key={product.snapshotId}
              className="rounded-2xl border border-[color:var(--site-border-soft)] bg-[var(--site-surface)] p-4"
            >
              <div className="mb-3 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-xs font-medium uppercase tracking-wide text-[var(--site-ink-muted)]">#{product.rank} · {marketplaceLabel(product.marketplace)} · {categoryLabel(product.categoryId)}</div>
                  <div className="mt-1 line-clamp-2 font-semibold text-[var(--site-ink)]">{product.title}</div>
                </div>
                <div className="shrink-0 rounded-full bg-[var(--site-primary-fill)] px-3 py-1 text-sm font-semibold text-[var(--site-primary-text)]">{formatRub(product.currentPriceRub)}</div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm text-[var(--site-ink-muted)] md:grid-cols-4">
                <div><span className="block text-xs">Бренд</span><span className="font-medium text-[var(--site-ink)]">{product.brandName ?? 'нет сигнала'}</span></div>
                <div><span className="block text-xs">Продавец</span><span className="font-medium text-[var(--site-ink)]">{product.sellerName ?? 'нет сигнала'}</span></div>
                <div><span className="block text-xs">Рейтинг</span><span className="font-medium text-[var(--site-ink)]">{formatRating(product.rating)}</span></div>
                <div><span className="block text-xs">Отзывы</span><span className="font-medium text-[var(--site-ink)]">{formatNumber(product.reviewCount)}</span></div>
              </div>
              {product.discountPercent !== null && product.discountPercent > 0 ? (
                <div className="mt-3 text-xs font-medium text-[var(--site-accent-text)]">Скидка {formatDiscountPercent(product.discountPercent)}</div>
              ) : null}
              <a
                href={product.sourceUrl}
                target="_blank"
                rel="noreferrer"
                aria-label={`Открыть источник: ${product.title}`}
                className="mt-4 inline-flex min-h-9 items-center rounded-full border border-[color:var(--site-border-soft)] px-3 text-sm font-semibold text-[var(--site-ink)] transition hover:bg-[var(--site-surface-muted)]"
              >
                Открыть источник
              </a>
            </article>
          ))}
        </div>
        {visibleProducts.length < products.length ? (
          <div className="mt-5 flex justify-center">
            <Button type="button" variant="outline" onClick={() => setVisibleProductCount((count) => count + 12)}>
              Показать ещё
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

const ONE_COLLECTION_DATE_NOTE =
  'Текущая публикация содержит одну дату сбора, поэтому ценовые, ранговые и динамические сигналы отзывов появятся после накопления ежедневной истории.';

function DataQuality({ dashboard }: { dashboard: MarketplaceIntelligenceDashboardPayload }) {
  const limitations = [...dashboard.collection.limitations];
  // The analyzer no longer emits the one-date limitation; while history is genuinely
  // a single date, the page itself states it (deduped against old stored artifacts,
  // which may arrive raw or already localized by the page).
  if (
    !dashboard.trendAvailability.dayOverDayAvailable &&
    !limitations.some(
      (limitation) =>
        limitation.startsWith('The current artifact is one collection date') || limitation === ONE_COLLECTION_DATE_NOTE,
    )
  ) {
    limitations.push(ONE_COLLECTION_DATE_NOTE);
  }
  if (limitations.length === 0) {
    limitations.push('Источник не сообщил дополнительных ограничений для последнего сбора.');
  }

  // Problem rows first; the payload's marketplace/category order is kept inside each group.
  const jobStatuses = [...dashboard.jobStatuses].sort(
    (left, right) => Number(left.status === 'complete') - Number(right.status === 'complete'),
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Качество данных и ограничения</CardTitle>
        <CardDescription>Отчёт показывает ограничения явно и не превращает отсутствующие источником поля в нули.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-5 lg:grid-cols-[1.2fr_0.8fr]">
        <div className="space-y-3">
          {limitations.slice(0, 6).map((limitation) => (
            <div key={limitation} className="rounded-2xl border border-[color:var(--site-border-soft)] bg-[var(--site-surface-muted)] px-4 py-3 text-sm text-[var(--site-ink-muted)]">
              {localizeMarketplaceIntelligenceLimitation(limitation)}
            </div>
          ))}
        </div>
        <div className="rounded-2xl border border-[color:var(--site-border-soft)] bg-[var(--site-surface)] p-4">
          <div className="flex items-center justify-between gap-3">
            <h3 className="font-semibold text-[var(--site-ink)]">Статус источников</h3>
            <span className="rounded-full bg-[var(--site-surface-muted)] px-2 py-1 text-xs text-[var(--site-ink-muted)]">
              {formatNumber(dashboard.summary.completeJobs)} из {formatNumber(dashboard.summary.totalJobs)} собрано
            </span>
          </div>
          <div className="mt-4 space-y-3 text-sm">
            {jobStatuses.map((job) => (
              <div key={`${job.marketplace}-${job.categoryId}-${job.queryId}`} className="flex items-start justify-between gap-3 border-b border-[color:var(--site-border-soft)] pb-3 last:border-0 last:pb-0">
                <div className="min-w-0">
                  <div className="font-medium text-[var(--site-ink)]">{marketplaceLabel(job.marketplace)} · {categoryLabel(job.categoryId)}</div>
                  <div className="text-xs text-[var(--site-ink-muted)]">{job.queryText}</div>
                  {job.status !== 'complete' && (job.statusReasonCode || job.statusReasonMessage) ? (
                    <div className="mt-1 text-xs text-amber-700">
                      {job.statusReasonCode}{job.statusReasonMessage ? ` — ${job.statusReasonMessage}` : ''}
                    </div>
                  ) : null}
                </div>
                <span
                  className={
                    job.status === 'complete'
                      ? 'rounded-full border border-[color:var(--site-border-soft)] px-2 py-1 text-xs text-[var(--site-ink-muted)]'
                      : 'rounded-full border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-800'
                  }
                >
                  {jobStatusLabel(job.status)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

type FocusCategory = { categoryId: string; label: string };

// Topics the visitor can focus the whole report on: only categories with collected
// data (querySummaries present). Below two such categories the control is pointless.
function buildFocusCategories(dashboard: MarketplaceIntelligenceDashboardPayload): FocusCategory[] {
  const categoryIds = new Set(dashboard.querySummaries.map((summary) => summary.categoryId));
  return [...categoryIds]
    .map((categoryId) => ({ categoryId, label: categoryLabel(categoryId) }))
    .sort((left, right) => left.label.localeCompare(right.label, 'ru'));
}

function FocusChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={
        active
          ? 'whitespace-nowrap rounded-full bg-[var(--site-primary-fill)] px-3 py-1.5 text-sm font-semibold text-[var(--site-primary-text)]'
          : 'whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium text-[var(--site-ink-muted)] transition hover:bg-[var(--site-surface-muted)] hover:text-[var(--site-ink)]'
      }
    >
      {label}
    </button>
  );
}

function ReportAnchorNav({
  dashboard,
  focusCategories,
  activeCategory,
  onCategoryChange,
}: {
  dashboard: MarketplaceIntelligenceDashboardPayload;
  focusCategories: FocusCategory[];
  activeCategory: string | null;
  onCategoryChange: (categoryId: string | null) => void;
}) {
  // The anchor nav keeps its floating shadow whenever it is pinned to the top
  // (content scrolling beneath it), independent of whether the header is hidden —
  // so the shadow doesn't blink off the moment the user scrolls up.
  const navRef = useRef<HTMLElement>(null);
  useStuckAttribute(navRef);

  const sections = [
    { id: 'mi-decisions', label: 'Решения', present: dashboard.decisionInsights.length > 0 },
    { id: 'mi-changes', label: 'Изменения', present: true },
    { id: 'mi-price-check', label: 'Моя цена', present: dashboard.querySummaries.some((summary) => summary.medianPriceRub !== null) },
    { id: 'mi-trends', label: 'Тренды', present: dashboard.significantProductTrends.length > 0 || dashboard.trendAvailability.dayOverDayAvailable },
    { id: 'mi-charts', label: 'Графики', present: buildChartRows(dashboard.querySummaries).hasAny },
    { id: 'mi-comparison', label: 'Сравнение', present: true },
    { id: 'mi-topics', label: 'Темы', present: dashboard.querySummaries.length > 0 },
    { id: 'mi-reference', label: 'Проверка данных', present: true },
  ].filter((section) => section.present);

  // The topic-focus row only appears when there are at least two data-bearing topics
  // to switch between; both rows scroll horizontally inside one sticky element.
  const showFocus = focusCategories.length >= 2;

  return (
    <nav ref={navRef} aria-label="Разделы отчёта" className="mi-anchor-nav sticky top-[var(--mi-anchor-nav-top)] z-40 space-y-1.5 py-1">
      {showFocus ? (
        <div className="mi-anchor-bar flex w-max max-w-full rounded-full border border-[color:var(--site-border-soft)] bg-[var(--site-surface-solid)] p-1.5">
          <div className="flex min-w-0 items-center gap-1 overflow-x-auto">
            <span className="whitespace-nowrap px-2 text-xs font-semibold uppercase tracking-wide text-[var(--site-ink-muted)]">Тема</span>
            <FocusChip label="Все темы" active={activeCategory === null} onClick={() => onCategoryChange(null)} />
            {focusCategories.map((category) => (
              <FocusChip
                key={category.categoryId}
                label={category.label}
                active={activeCategory === category.categoryId}
                onClick={() => onCategoryChange(category.categoryId)}
              />
            ))}
          </div>
        </div>
      ) : null}
      <div className="mi-anchor-bar flex w-max max-w-full rounded-full border border-[color:var(--site-border-soft)] bg-[var(--site-surface-solid)] p-1.5">
        <div className="flex min-w-0 gap-1 overflow-x-auto">
          {sections.map((section) => (
            <a
              key={section.id}
              href={`#${section.id}`}
              className="whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium text-[var(--site-ink-muted)] transition hover:bg-[var(--site-surface-muted)] hover:text-[var(--site-ink)]"
            >
              {section.label}
            </a>
          ))}
        </div>
      </div>
    </nav>
  );
}

export function MarketplaceIntelligencePublicReport({ dashboard }: { dashboard: MarketplaceIntelligenceDashboardPayload }) {
  const focusCategories = useMemo(() => buildFocusCategories(dashboard), [dashboard]);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  // Hide the global site header on scroll-down and reveal it on scroll-up,
  // letting the anchor nav slide up into the reclaimed space (see globals.css).
  useNavAutoHide();

  return (
    <PageSection className="gap-6">
      <StaleReportNotice dashboard={dashboard} />
      <ReportAnchorNav
        dashboard={dashboard}
        focusCategories={focusCategories}
        activeCategory={activeCategory}
        onCategoryChange={setActiveCategory}
      />
      <DecisionMap dashboard={dashboard} activeCategory={activeCategory} />
      <DailyChangesSection dashboard={dashboard} activeCategory={activeCategory} />
      <PriceCheckSection dashboard={dashboard} activeCategory={activeCategory} />
      <SignificantProductTrends dashboard={dashboard} activeCategory={activeCategory} />
      <DecisionEvidenceCharts dashboard={dashboard} activeCategory={activeCategory} />
      <MarketplaceComparison dashboard={dashboard} activeCategory={activeCategory} />
      <TopicCoverageSection dashboard={dashboard} activeCategory={activeCategory} />

      <Card id="mi-reference" className="scroll-mt-52 border-[color:var(--site-border-soft)] bg-[var(--site-surface-muted)]">
        <CardHeader>
          <SectionPill className="self-start">Проверить данные</SectionPill>
          <SectionHeading>Подробности для проверки</SectionHeading>
          <CardDescription>
            Основные решения уже выше. Ниже — справочные списки, методология и ограничения для тех, кто хочет проверить исходные срезы.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <DetailsSection title="Категории и запросы" description="Подробная матрица по площадкам, категориям, продавцам, брендам и медианной цене.">
            <QueryMatrix dashboard={dashboard} />
          </DetailsSection>
          <DetailsSection title="Товары в топе" description="Кураторская выборка карточек; не весь датасет.">
            <TopProducts dashboard={dashboard} />
          </DetailsSection>
          <DetailsSection title="Качество данных и методология" description="Что собрано, какие поля источник не раскрыл и почему отчёт не рисует синтетические строки.">
            <div className="space-y-4">
              <DataQuality dashboard={dashboard} />
              <MethodologyNote dashboard={dashboard} />
            </div>
          </DetailsSection>
          {!dashboard.trendAvailability.sevenDayAvailable ? (
            <DetailsSection title="Что появится с историей" description="Тренды не строятся из одного снимка; они появятся после накопления ежедневной истории.">
              {dashboard.significantProductTrends.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-[color:var(--site-border-mid)] p-4 text-sm text-[var(--site-ink-muted)]">
                  Истории по exact product ID пока недостаточно: графики карточек появятся после двух опубликованных снимков с ценой.
                </div>
              ) : (
                <div className="grid gap-3 md:grid-cols-3">
                  {['Более длинный ценовой тренд за 7–14 дней', 'Движение в ранге и новые продавцы', 'Отзывы и наличие за 7 дней'].map((item) => (
                    <div key={item} className="rounded-2xl border border-dashed border-[color:var(--site-border-mid)] p-4 text-sm text-[var(--site-ink-muted)]">
                      <div className="font-semibold text-[var(--site-ink)]">{item}</div>
                      <div className="mt-2">появится после накопления ежедневной истории</div>
                    </div>
                  ))}
                </div>
              )}
            </DetailsSection>
          ) : null}
        </CardContent>
      </Card>

      <div className="flex flex-wrap gap-3">
        <Button asChild>
          <a href="/templates/">Посмотреть шаблоны сбора</a>
        </Button>
        <Button asChild variant="outline">
          <a href="/contact/">Обсудить аналитику для своей категории</a>
        </Button>
      </div>
    </PageSection>
  );
}
